import { lazy, useState, type ComponentType } from 'react';

type Loader<P> = () => Promise<{ default: ComponentType<P> }>;

export type PreloadableComponent<P> = ComponentType<P> & { preload: () => Promise<void> };

// React.lazy suspends on its first render even when the chunk has already
// loaded. On a pre-rendered page that swaps the visible content for the
// Suspense fallback while React mounts, and the footer jumps (layout shift).
// Once preload() has resolved, this renders the loaded component directly.
export function lazyWithPreload<P extends object>(loader: Loader<P>): PreloadableComponent<P> {
  let loaded: ComponentType<P> | undefined;
  const load = () =>
    loader().then((module) => {
      loaded = module.default;
      return module;
    });
  const Lazy = lazy(load);

  const Preloadable = ((props: P) => {
    // Fixed per instance: switching between the two component types on a
    // later render would remount the page and reset its state.
    const [Initial] = useState(() => loaded);
    return Initial ? <Initial {...props} /> : <Lazy {...props} />;
  }) as PreloadableComponent<P>;

  Preloadable.preload = () => load().then(() => undefined);
  return Preloadable;
}
